import { isNil, toNumber } from 'lodash';

import { CalendarDate, isAfter, isEqual } from '../dateutils';
import { getDateOfNextInvoice } from '../leaseutils';
import { IntegrationConfigurationDTO } from '../types/dto-app';
import {
  Integration,
  IntegrationConfiguration,
  IntegrationName,
  IntegrationStatus,
  LeasePaymentFrequency,
  LeasePaymentInvoicePeriod,
} from '../types/model-app';

import { exactOnlineDefaultConfigurations } from './integrationConfigurations/exactOnline';
import {
  INTEGRATION_CONFIG_PAYMENT_PROVIDER_ALLOW_OWNER_ACCOUNT,
  INTEGRATION_CONFIG_PAYMENT_PROVIDER_DEFAULT_FOR_NEW_LEASES,
  PAYMENT_PROVIDER_DEFAULT_CONFIGURATIONS,
} from './integrationConfigurations/paymentProvider';
import { peppolDefaultConfigurations } from './integrationConfigurations/peppol';

import {
  bob50DefaultConfigurations,
  googleDefaultConfigurations,
  icloudDefaultConfigurations,
  microsoftDefaultConfigurations,
  myBanxDefaultConfigurations,
  ocrDefaultConfigurations,
  odooDefaultConfigurations,
  pontoDefaultConfigurations,
} from './index';

// Categories
export enum IntegrationCategory {
  ACCOUNTING = 'ACCOUNTING',
  BANKING = 'BANKING',
  AI = 'AI',
  CLOUD = 'CLOUD',
}

export type IntegrationCategories = {
  name: IntegrationName;
  categories: IntegrationCategory[];
};

export enum IntegrationNotificationMessageType {
  INTEGRATION_DISCONNECTED = 'INTEGRATION_DISCONNECTED',
  BANKING_NEW_BANK_ACCOUNT = 'BANKING_NEW_BANK_ACCOUNT',
}

export const INTEGRATIONS_WITH_CATEGORIES: IntegrationCategories[] = [
  { name: IntegrationName.PONTO, categories: [IntegrationCategory.BANKING] },
  { name: IntegrationName.MYBANX, categories: [IntegrationCategory.BANKING] },
  { name: IntegrationName.SAGE_BOB50, categories: [IntegrationCategory.ACCOUNTING] },
  { name: IntegrationName.EXACT_ONLINE, categories: [IntegrationCategory.ACCOUNTING, IntegrationCategory.BANKING] },
  { name: IntegrationName.OCR, categories: [IntegrationCategory.AI] },
  { name: IntegrationName.ICLOUD, categories: [IntegrationCategory.CLOUD] },
  { name: IntegrationName.MICROSOFT, categories: [IntegrationCategory.CLOUD] },
  { name: IntegrationName.ODOO, categories: [IntegrationCategory.CLOUD] },
  { name: IntegrationName.GOOGLE, categories: [IntegrationCategory.CLOUD] },
  { name: IntegrationName.PEPPOL, categories: [IntegrationCategory.ACCOUNTING] },
  { name: IntegrationName.PAYMENT_PROVIDER, categories: [IntegrationCategory.BANKING] },
];

const DEFAULT_CONFIGURATIONS_VALUES: { [key in IntegrationName]: { [config: string]: string } } = {
  [IntegrationName.CODA]: {},
  [IntegrationName.SAGE_BOB50]: {
    ...bob50DefaultConfigurations,
  },
  [IntegrationName.PONTO]: {
    ...pontoDefaultConfigurations,
  },
  [IntegrationName.MYBANX]: {
    ...myBanxDefaultConfigurations,
  },
  [IntegrationName.OCR]: {
    ...ocrDefaultConfigurations,
  },
  [IntegrationName.EXACT_ONLINE]: {
    ...exactOnlineDefaultConfigurations,
  },
  [IntegrationName.ICLOUD]: {
    ...icloudDefaultConfigurations,
  },
  [IntegrationName.MICROSOFT]: {
    ...microsoftDefaultConfigurations,
  },
  [IntegrationName.GOOGLE]: {
    ...googleDefaultConfigurations,
  },
  [IntegrationName.ODOO]: {
    ...odooDefaultConfigurations,
  },
  [IntegrationName.PEPPOL]: {
    ...peppolDefaultConfigurations,
  },
  [IntegrationName.PAYMENT_PROVIDER]: {
    ...PAYMENT_PROVIDER_DEFAULT_CONFIGURATIONS,
  },
};

/**
 * Get the configuration value on the configurations array for the name
 * @param configurations
 * @param configName
 * @returns {string} the setting value
 */
export const getConfigurationValueOrDefault = (
  integrationName: IntegrationName,
  configurations: IntegrationConfiguration[] | null | undefined,
  configName: string
): string | null => {
  if (
    isNil(DEFAULT_CONFIGURATIONS_VALUES[integrationName]) ||
    isNil(DEFAULT_CONFIGURATIONS_VALUES[integrationName][configName])
  ) {
    return null;
  }

  const configuration = getConfigurationValue(configName, configurations);
  if (configuration) {
    return configuration;
  }

  const defaultValue = DEFAULT_CONFIGURATIONS_VALUES[integrationName][configName];
  if (defaultValue) {
    return `${defaultValue}`;
  }
  return null;
};

export const getConfigurationValue = (
  configName: string,
  configurations?: IntegrationConfiguration[] | null
): string | null => configurations?.find((config) => config.name === configName)?.value ?? null;

export const isConfigurationTrue = (configName: string, configurations?: IntegrationConfiguration[] | null) =>
  getConfigurationValue(configName, configurations) === 'true';

export const isPaymentProviderAllowOwnerDifferentAccountEnabled = (
  configurations?: IntegrationConfiguration[] | null
) =>
  getConfigurationValueOrDefault(
    IntegrationName.PAYMENT_PROVIDER,
    configurations,
    INTEGRATION_CONFIG_PAYMENT_PROVIDER_ALLOW_OWNER_ACCOUNT
  ) === 'true';

export const isPaymentProviderEnabledForAllNewLeasesByDefault = (configurations?: IntegrationConfiguration[] | null) =>
  getConfigurationValueOrDefault(
    IntegrationName.PAYMENT_PROVIDER,
    configurations,
    INTEGRATION_CONFIG_PAYMENT_PROVIDER_DEFAULT_FOR_NEW_LEASES
  ) === 'true';

export const doesProcessNeedsToBeExecuted = (
  activationDate: CalendarDate,
  lastRunDate: CalendarDate | null | undefined,
  frequency: LeasePaymentFrequency | keyof typeof LeasePaymentFrequency,
  frequencyValue: string,
  executionDate: CalendarDate
) => {
  const isDateAfterActivation = isAfter(executionDate, activationDate);
  if (!isDateAfterActivation) {
    return false;
  }

  if (!lastRunDate) {
    // If not yet processed
    return true;
  }

  const nextDateThatNeedsToBeProcessed = getDateOfNextExecution(activationDate, lastRunDate, frequency, frequencyValue);

  const dateIsAfterNextExecutionDay =
    isAfter(executionDate, nextDateThatNeedsToBeProcessed) || isEqual(executionDate, nextDateThatNeedsToBeProcessed);

  return dateIsAfterNextExecutionDay;
};

export const getDateOfNextExecution = (
  activationDate: CalendarDate,
  lastRunDate: CalendarDate | null | undefined,
  frequency: LeasePaymentFrequency | keyof typeof LeasePaymentFrequency,
  frequencyValue: string
): CalendarDate =>
  getDateOfNextInvoice(
    activationDate,
    lastRunDate,
    frequency,
    LeasePaymentInvoicePeriod.CUSTOM_PERIOD,
    toNumber(frequencyValue)
  );

export const isAccountingIntegrationActive = (integrations?: Integration[] | null): boolean => {
  if (!integrations) {
    return false;
  }

  const activeIntegration = integrations.find(
    (integration) =>
      integration.status !== IntegrationStatus.INACTIVE &&
      isIntegrationNameFromCategory(integration.name as IntegrationName, IntegrationCategory.ACCOUNTING)
  );
  return Boolean(activeIntegration);
};

const isIntegrationNameFromCategory = (integrationName: IntegrationName, category: IntegrationCategory) => {
  return INTEGRATIONS_WITH_CATEGORIES.some(
    (integrationWithCategory) =>
      integrationName === integrationWithCategory.name && integrationWithCategory.categories.includes(category)
  );
};

export const getIntegrationNamesFromCategory = (category: IntegrationCategory) => {
  return INTEGRATIONS_WITH_CATEGORIES.filter((integrationWithCategory) =>
    integrationWithCategory.categories.includes(category)
  ).map((integrationWithCategory) => integrationWithCategory.name);
};

export const isIntegrationActive = (integration?: Pick<Integration, 'status'> | null) =>
  Boolean(integration && integration.status !== IntegrationStatus.INACTIVE);

export const isOneIntegrationFromCategoryActive = (
  integrations: Integration[] | null | undefined,
  category: IntegrationCategory
): boolean => {
  if (!integrations) {
    return false;
  }

  const activeIntegration = integrations.find(
    (integration) =>
      isIntegrationActive(integration) && isIntegrationNameFromCategory(integration.name as IntegrationName, category)
  );
  return Boolean(activeIntegration);
};

export const replaceConfigurationValue = (
  configurations: IntegrationConfigurationDTO[] | null | undefined,
  configName: string,
  newValue: string
): IntegrationConfiguration[] => {
  return (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    configurations?.map(({ __typename, ...configuration }) =>
      configuration.name === configName ? { ...configuration, value: newValue } : configuration
    ) ?? []
  );
};

export const upsertConfigurationValue = (
  configurations: (IntegrationConfiguration & { __typename?: string })[] | null | undefined,
  configName: string,
  newValue: string
): IntegrationConfiguration[] => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cleanedConfigurations = configurations?.map(({ __typename, ...configuration }) => configuration) ?? [];
  const configurationExists = cleanedConfigurations.some((configuration) => configuration.name === configName);
  if (configurationExists) {
    return cleanedConfigurations.map((configuration) =>
      configuration.name === configName ? { ...configuration, value: newValue } : configuration
    );
  }
  return [...cleanedConfigurations, { name: configName, value: newValue }];
};
